import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import {
  GOT_TOURNAMENT_ACTIVE_TABLES_V2,
  GOT_TOURNAMENT_PRIZES,
  GOT_TOURNAMENT_RANKING,
  GOT_TOURNAMENT_STRUCTURE
} from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import GameContext from 'contexts/GameContext';
import { useGridMode } from 'contexts/GridModeContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import Decimal from 'decimal.js';
import { useFeatureFlag } from 'featureFlags';
import { useAppSelector } from 'hooks';
import { useGuest } from 'hooks/useGuest';
import { useOrientation } from 'hooks/useOrientation';
import { useSngPayouts } from 'hooks/useSngPayouts';
import { useStripShownSlots, useStripSlotsInUse } from 'hooks/useStripSlots';
import { useSupportsHover } from 'hooks/useSupportsHover';
import { useTableScopedOpen } from 'hooks/useTableScopedOpen';
import { useTableSelector } from 'hooks/useTableSelector';
import useGameService from 'services/GameService';
import {
  currencyFormatter,
  decompressData,
  humanizeCountdown,
  secondsToTime,
  toChips
} from 'utils/functions';
import { pickLiveTournamentRow } from 'utils/pickLiveTournamentRow';
import { isExitedTable } from 'utils/recentlyExitedTables';
import { swipeIgnoreProps } from 'utils/swipeIgnore';
import { holdsTableSlot, MAX_OPEN_TABLES } from 'utils/tableCapacity';

import { payoutPool } from 'components/TournamentPayouts/payouts';
import TournamentPayouts from 'components/TournamentPayouts/TournamentPayouts';

import { compactChips } from './TournamentTableDetails';

type TournamentStructure = {
  tournamentId: number;
  bb: number;
  isBreak: number;
  level: string;
  lvlDisplay: number;
  lvlTitle: string;
  mins: number;
  num: string;
  sb: number;
  ante: number;
  lateRegistrationCountDown: number;
  currentLevel: string;
  nextLevel: number;
};

type PropType = {
  sngPayouts?: ReturnType<typeof useSngPayouts>;
  maxSeats?: number;
  bountyAmount?: number;
  gameTitle: string;
  tournamentId: number;
  tableType: string;
  tournamentStatus: string;
  tournamentPrizePool: number;
  // When provided, the panel's open/close is driven externally (e.g. the
  // lobby's "Tournament Info" button) instead of GameContext. This lets the
  // same dashboard render outside a table, where GameContext has no live
  // provider. Omitted → falls back to GameContext.showTournamentDetails, so
  // the in-table usage is unchanged.
  open?: boolean;
  onClose?: () => void;
  // Set when the dashboard is rendered from the portrait-locked lobby (wrapped
  // in ForcePortrait), so on mobile-landscape it's rotated 90°. Inside that
  // rotated box the vertical space is the device WIDTH, so the panel's `85vh`
  // height cap resolves to the short landscape side and clips the content —
  // budget against 85vw instead. Omitted for the in-table felt usage (not
  // rotated), where 85vh is correct.
  forcePortrait?: boolean;
  // The add-on from the tournament's lobby record, for lobby callers. The
  // status feed carries no price, and before play it may carry no row at all
  // for the tournament, so the lobby record is the only source there. Omitted
  // in a table, where that table's own snapshot supplies it.
  lobbyAddOn?: LobbyAddOn;
};

export type LobbyAddOn = {
  count?: unknown;
  price?: unknown;
  chips?: unknown;
  schedule?: unknown;
  isFinalMultiFlight?: unknown;
};

type Tab = 'ranking' | 'prizes' | 'tables' | 'blinds';

/**
 * Decide whether the client timers should resync from the server push, or
 * hold the local clock and let it catch up smoothly.
 *
 * The original gate used only `elapsedTime` drift, which is fine for elapsed
 * (monotonic) but misses level rollovers: when the local levelCountDown ticks
 * to 0 and the engine moves to the next level, the server pushes a fresh
 * countdown (e.g. 1800s) — but elapsed delta stays small, so the gate refuses
 * to sync and the UI sticks at 00:00.
 *
 * Adding the `localLevel === 0 && serverLevel > 0` clause catches exactly
 * that rollover edge without over-syncing during normal ticking (where local
 * and server level deltas are within ±1s).
 *
 * Exported for unit testing.
 */
export function decideTimerSync(
  localElapsed: number,
  localLevel: number,
  serverElapsed: number,
  serverLevel: number,
  threshold = 2
): { shouldSync: boolean } {
  const isFirstSync = localElapsed === 0;
  const elapsedDelta = serverElapsed - localElapsed; // positive = server ahead
  const isLevelRollover = localLevel === 0 && serverLevel > 0;
  const shouldSync =
    isFirstSync || elapsedDelta >= 1 || Math.abs(elapsedDelta) > threshold || isLevelRollover;
  return { shouldSync };
}

/**
 * Build the label + value for the "Player / Entry / AddOn" stat box.
 *
 * The AddOn segment is shown when the tournament offers add-ons
 * (`liveStatus.addOn > 0`) and dropped otherwise. The "has any been taken
 * yet" count (`totalAddOns`) is irrelevant to the gate — a tournament that
 * offers add-ons should always show the column, even at a running count of
 * zero, so players can see the dimension exists. SnGs and MTTs without
 * add-ons have `addOn === 0` and naturally collapse to "Player / Entry".
 *
 * Exported for unit testing — the parent component is bound up in a lot
 * of socket / Redux state that we don't want to drag into pure-logic tests.
 */
export function formatPlayerEntryAddonStat(
  liveStatus:
    | {
        addOn?: number;
        totalAddOns?: number;
        activePlayers?: number;
        registeredPlayers?: number;
        totalRebuys?: number;
      }
    | null
    | undefined,
  isPlaying: boolean
): { label: string; value: string } {
  const showAddOn = Number(liveStatus?.addOn ?? 0) > 0;
  const label = showAddOn ? 'Player / Entry / AddOn' : 'Player / Entry';
  const active = liveStatus?.activePlayers ?? 0;
  const registered =
    (Number(liveStatus?.registeredPlayers) || 0) + (Number(liveStatus?.totalRebuys) || 0);
  if (!isPlaying) return { label, value: '-' };
  const value = showAddOn
    ? `${active}/${registered}/${liveStatus?.totalAddOns ?? 0}`
    : `${active}/${registered}`;
  return { label, value };
}

/**
 * The Add-On row's value: what the add-on costs and how many chips it buys,
 * e.g. "$5.00 for 10,000 chips" (EN-10960). Players could only find this in
 * the lobby, so a seated player had no way to know how much to deposit before
 * the short add-on break.
 *
 * Null — no row — whenever the engine would never offer the add-on, mirroring
 * the tournament-level gates of canAddOnChips (engine texas/socket.js and
 * serverless broadcastTournamentDetails): no add-on configured (`addOn` 0), no
 * chips granted, a Rolling schedule, or the final day of a multi-flight. A row
 * there would have players deposit for a button that never appears. The
 * per-hand gates (level, break, hand over) are timing, not availability.
 *
 * `price` is the full charge (the engine debits addOnPrice alone); when it is
 * unknown the chips still show on their own. `chips` may arrive comma-formatted
 * ("10,000") from the MTT table feed. The chip count is exact, not
 * K-abbreviated: it is the answer to "how many chips do I get".
 *
 * Exported for unit testing.
 */
export function formatAddOnInfo({
  addOn,
  price,
  chips,
  schedule,
  isFinalMultiFlight
}: {
  addOn?: unknown;
  price?: unknown;
  chips?: unknown;
  schedule?: unknown;
  isFinalMultiFlight?: unknown;
}): string | null {
  if (!(Number(addOn) > 0)) return null;
  if (schedule === 'Rolling' || Number(isFinalMultiFlight) === 1) return null;
  const chipCount = toChips((chips ?? 0) as number | string);
  if (chipCount <= 0) return null;
  const chipText = `${chipCount.toLocaleString('en-US')} chips`;
  const priceKnown = price !== null && price !== undefined && price !== '';
  return priceKnown ? `${currencyFormatter(price as number | string)} for ${chipText}` : chipText;
}

/**
 * Whether the table this dashboard reads through useTableSelector belongs to
 * the tournament it is showing. From the lobby the selector falls back to
 * `state.currentTable`, which can be another tournament's (or a cash) table, so
 * its add-on fields must not be shown against this tournament.
 *
 * Exported for unit testing.
 */
export const isTableOfTournament = (linkedId: unknown, tournamentId: unknown): boolean => {
  const id = Number(tournamentId);
  return Number.isFinite(id) && id > 0 && Number(linkedId) === id;
};

// Exported for unit testing; shared with the Sit & Go waiting felt (EN-11257).
export { pickLiveTournamentRow };

/**
 * The table this dashboard is being viewed FROM, but only while that table is
 * still open. Exported for unit testing.
 *
 * `game.gameID` reaches TournamentDetails through useTableSelector. Inside a
 * table panel that is the panel's own table. From the LOBBY's Tournament Info
 * modal there is no table context, so the hook falls back to
 * `state.currentTable` — and nothing resets that slice when the player exits
 * their LAST table (returnToLobby only navigates). Trusting it blindly left the
 * just-exited table's row stuck on "Observed" after a mobile Exit Table. With a
 * second table still open the hop refocused currentTable onto it, which is why
 * only the single-table exit showed the bug. An id that is not in state.tables
 * is not a table the player has open, whatever the fallback slice remembers.
 */
export const resolveObservedCurrentTable = (
  gameID: unknown,
  openTableIds: Set<string>
): number | undefined => {
  const id = Number(gameID);
  if (!Number.isFinite(id) || id <= 0) return undefined;
  return openTableIds.has(String(id)) ? id : undefined;
};

/**
 * Whether the player already holds MAX_OPEN_TABLES (4) slots, so Observe on a
 * table they aren't watching should be disabled — at the same count the
 * header's "+" hides at (open tables, waitlist cards, Sit & Go registrations,
 * seats listed but not loaded yet, a rebalance in flight; exited tables left
 * out), read from the mounted strip rather than deriving it again (EN-11131).
 * `openTables`: the open, non-exited tables, counted on their own in case no
 * strip is mounted to publish.
 */
export const useObserveCapReached = (openTables = 0): boolean =>
  Math.max(useStripSlotsInUse(), openTables) >= MAX_OPEN_TABLES;

function TournamentDetails({
  tournamentId,
  gameTitle,
  tableType,
  tournamentStatus,
  tournamentPrizePool,
  open,
  onClose,
  forcePortrait = false,
  lobbyAddOn,
  sngPayouts,
  maxSeats,
  bountyAmount
}: PropType) {
  const { setShowTournamentDetails, showTournamentDetails } = useContext(GameContext);
  // Mirror ForcePortrait's lock condition (see ForcePortrait.tsx): from the
  // lobby this dashboard is rotated 90° on mobile-landscape, so its height cap
  // must budget against the device WIDTH (85vw), not 85vh (the short side).
  const supportsHover = useSupportsHover();
  const orientation = useOrientation();
  const portraitLocked = forcePortrait && !supportsHover && orientation === 'landscape';
  // Prefer the externally-controlled open/close when supplied (lobby usage);
  // otherwise fall back to GameContext (in-table usage) — unchanged behavior.
  // Scoped to its own table: this dashboard is position-fixed, so a parked
  // table's open details would cover the table the player swiped to. Closing it
  // here also drops that table's details subscription (see the effect below).
  const isOpen = useTableScopedOpen(open ?? showTournamentDetails);
  const closeDetails = useCallback(
    () => (onClose ? onClose() : setShowTournamentDetails(false)),
    [onClose, setShowTournamentDetails]
  );
  const { openTable, selectGridTable } = useContext(ViewManagerContext);
  const { socket } = useContext(SocketContext);
  const isGridMode = useGridMode();
  const {
    structures,
    tournamentPrizes,
    unsubscribeTournamentDetails,
    subscribeTournamentDetails,
    joinTable
  } = useGameService();
  const guid = useAppSelector((state) => state.user.playerGUID);
  const tablesById = useAppSelector((state) => (state as any).tables) as Record<string, any>;
  const game = useTableSelector((table) => table.game);
  const tournamentDetailStatus = useTableSelector((table) => table.tournamentStatus);
  const guest = useGuest();
  const showAddOnInfo = useFeatureFlag('tournamentAddOnInfo');
  const sngPayoutEnabled = useFeatureFlag('sngPayoutStructure');
  const showSngPayouts = sngPayoutEnabled && tableType === 't';

  // Active players in THIS tournament must not observe other tables — hide
  // the Observe column entirely in TablesTab. Busted ('F') and
  // non-registered players keep full Observe access. The matching
  // server-side guard is the redirect effect in pages/Game.tsx, which
  // catches manual URL navigation to a wrong table.
  //
  // Read the per-player record from `tournamentDetailStatus` (the table's
  // own socket state) rather than `playerTournamentDetails` (the lobby
  // slice). The lobby slice is only populated by the GOT_PLAYERTOURNAMENTDETAILS
  // listener mounted inside Lobby.tsx — when the user reloads directly
  // into a table or sits there long enough, that slice is stale or empty
  // and the `status === 'P'` check returned false even for active SNG /
  // MTT players, leaking the Observe column. The table-level
  // tournamentStatus is kept fresh by the table's own subscription.
  const isActivePlayer = useMemo(() => {
    const records = Array.isArray(tournamentDetailStatus) ? tournamentDetailStatus : [];
    const detail = records.find(
      (d: any) => Number(d.tournamentId) === Number(tournamentId) && d.playerId === guid
    );
    return detail?.status === 'P';
  }, [tournamentDetailStatus, tournamentId, guid]);

  // Set of gameIDs the player currently has open (seated or observing).
  // Drives the "Observed" label vs. "Observe" button in TablesTab.
  const openTableIds = useMemo(() => {
    return new Set(
      Object.entries(tablesById || {})
        .filter(([, t]: [string, any]) => t?.game?.gameID && t.game.gameID !== 0)
        .map(([id]) => id)
    );
  }, [tablesById]);

  // See resolveObservedCurrentTable: a stale currentTable fallback must not
  // keep an exited table's row on "Observed".
  const currentGameID = resolveObservedCurrentTable(game?.gameID, openTableIds);

  // At-cap check (useObserveCapReached): the Observe button on rows the player
  // isn't already watching is disabled. Re-observing an already-open table
  // stays clickable because openTable treats it as a navigate (no slot
  // consumed), and so does a table the strip already holds a slot for (a
  // rebalance destination) for that game's table: opening it uses that slot.
  const isAtCap = useObserveCapReached(
    Array.from(openTableIds).filter((id) => !isExitedTable(id)).length
  );
  const stripShown = useStripShownSlots();
  const holdsSlot = useCallback(
    (gameID: string, game: string) =>
      Boolean(stripShown && holdsTableSlot(stripShown, stripShown.gameIDs, gameID, game)),
    [stripShown]
  );

  const [selectedTab, setSelectedTab] = useState<Tab>('ranking');

  // Let other parts of the app open this dashboard straight to the Tables list
  // — used when a busted/observing player's table has closed and they need to
  // pick another live table to observe (bust modal "Observe Game").
  useEffect(() => {
    const openToTables = (e: Event) => {
      // Scope to THIS cell. The event carries the gameID of the cell whose
      // "Observe Game" was clicked; ignore it unless it targets this cell (or
      // carries no gameID, for backward-compat). This stops a sibling grid cell
      // of the SAME tournament from also opening its tables list.
      const targetGid = (e as CustomEvent)?.detail?.gameID;
      if (
        targetGid !== undefined &&
        targetGid !== null &&
        String(targetGid) !== String(game?.gameID)
      ) {
        return;
      }
      setSelectedTab('tables');
      setShowTournamentDetails(true);
    };
    window.addEventListener('open-tournament-tables', openToTables);
    return () => window.removeEventListener('open-tournament-tables', openToTables);
  }, [setShowTournamentDetails, game?.gameID]);
  const [rankPlayers, setRankPlayers] = useState<any[]>([]);
  const [activeTables, setActiveTables] = useState<any[]>([]);
  const [prizes, setPrizes] = useState<any[]>([]);
  const [structuresData, setStructuresData] = useState<TournamentStructure[]>([]);

  // Live tournament status derived from socket updates
  const liveStatus = useMemo(() => {
    try {
      // Coerce ids (the feed can send tournamentId as a string). Selection rules
      // and the freshness NaN-guard live in pickLiveTournamentRow (unit tested).
      const picked = pickLiveTournamentRow(tournamentDetailStatus, tournamentId, guid);
      if (!picked) return null;
      const s = picked.row as any;
      const safeDecimal = (v: any) => {
        try {
          return new Decimal(v || '0').toDP(2).toNumber();
        } catch {
          return 0;
        }
      };
      return {
        status: s.tStatus ?? tournamentStatus,
        activePlayers: s.activePlayers ?? 0,
        registeredPlayers: s.registeredPlayers ?? 0,
        prizePool: s.prizepool ?? tournamentPrizePool ?? 0,
        currentLevel: s.currentLevel ?? 0,
        levelCountDown: s.levelCountDown ?? '-',
        elapsedTime: s.elapsedTime ?? 0,
        lateRegistrationCountDown: s.lateRegistrationCountDown ?? 0,
        isBreak: !!s.isBreak,
        isOnBreak: !!s.isOnBreak,
        isMultiFlightBreak: !!s.isMultiFlightBreak,
        isPostBalancing: s.isPostBalancing,
        totalAddOns: s.totalAddOns ?? 0,
        totalRebuys: s.totalRebuys ?? 0,
        // Configured add-ons per player (numeric count from the engine).
        // The stat box gates the AddOn column on this value being > 0 —
        // it's the canonical "this tournament offers add-ons" signal,
        // independent of how many add-ons have actually been taken.
        addOn: Number(s.addOn ?? 0),
        addOnChips: s.addOnChips,
        schedule: s.schedule,
        isFinalMultiFlight: s.isFinalMultiFlight,
        maxSeat: s.maxSeat,
        bountyAmount: s.bountyAmount,
        average: Math.trunc(safeDecimal(s.averageChips)),
        largest: Math.trunc(safeDecimal(s.largestChips)),
        smallest: Math.trunc(safeDecimal(s.smallestChips)),
        total: safeDecimal(s.totalChips)
      };
    } catch (e) {
      console.warn('[TournamentDetails] liveStatus parse error', e);
      return null;
    }
  }, [tournamentDetailStatus, tournamentId, tournamentStatus, tournamentPrizePool, guid]);

  const status = liveStatus?.status ?? tournamentStatus;
  const isPlaying = status === 'P';
  useEffect(() => {
    if (!showSngPayouts && !isPlaying && selectedTab === 'prizes') setSelectedTab('ranking');
  }, [showSngPayouts, isPlaying, selectedTab]);
  // Add-ons are MTT-only (engine canAddOnChips). The live status row is the
  // freshest offer but has no price; this tournament's own table and the lobby
  // record fill the gaps. The table counts only while it is still open
  // (currentGameID): from the lobby the selector can fall back to an exited
  // table's stale snapshot, even one of this same tournament.
  const ownTable =
    currentGameID !== undefined && isTableOfTournament(game?.linkedId, tournamentId)
      ? game
      : undefined;
  const addOnInfo =
    showAddOnInfo && tableType === 'm'
      ? formatAddOnInfo({
          addOn: liveStatus?.addOn ?? ownTable?.addOn ?? lobbyAddOn?.count,
          price: ownTable?.addOnPrice ?? lobbyAddOn?.price,
          chips: liveStatus?.addOnChips ?? ownTable?.addOnChips ?? lobbyAddOn?.chips,
          schedule: liveStatus?.schedule ?? ownTable?.schedule ?? lobbyAddOn?.schedule,
          isFinalMultiFlight:
            liveStatus?.isFinalMultiFlight ??
            ownTable?.isFinalMultiFlight ??
            lobbyAddOn?.isFinalMultiFlight
        })
      : null;
  // The engine pauses level/elapsed clocks during breaks and post-balancing.
  // Stop the local tick whenever any of those flags is set so the displayed
  // values stay in sync with the server instead of drifting forward locally.
  const isPaused =
    !!liveStatus?.isOnBreak ||
    !!liveStatus?.isBreak ||
    !!liveStatus?.isMultiFlightBreak ||
    !!liveStatus?.isPostBalancing;

  // ── Client-side timers — sync from API, tick locally every second ──
  const [localLevelCountDown, setLocalLevelCountDown] = useState(0);
  const [localElapsedTime, setLocalElapsedTime] = useState(0);
  const [localLateRegCountDown, setLocalLateRegCountDown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Mirror level into a ref so the sync useEffect can read its current value
  // without re-running each time the level ticks (which would defeat the
  // purpose of the local interval).
  const localLevelRef = useRef(0);
  useEffect(() => {
    localLevelRef.current = localLevelCountDown;
  }, [localLevelCountDown]);

  // Parse "HH:MM:SS" or "MM:SS" string to total seconds
  const parseCountdown = useCallback((val: any): number => {
    if (typeof val === 'number') return val;
    if (typeof val !== 'string' || val === '-') return 0;
    const parts = val.split(':').map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return 0;
  }, []);

  // Sync from API — all three timers are decided atomically off ONE drift check.
  // Elapsed is the canonical monotonic clock; level rollovers are caught
  // separately by `decideTimerSync` since the local level clamps at 0 and
  // would otherwise stay there when the engine moves to the next level.
  useEffect(() => {
    if (!liveStatus) return;

    const serverLevel = parseCountdown(liveStatus.levelCountDown);
    const serverElapsed = liveStatus.elapsedTime;
    const serverLateReg = liveStatus.lateRegistrationCountDown;

    setLocalElapsedTime((localElapsed) => {
      const { shouldSync } = decideTimerSync(
        localElapsed,
        localLevelRef.current,
        serverElapsed,
        serverLevel
      );
      if (shouldSync) {
        // Atomically push all three to the latest server values.
        setLocalLevelCountDown(serverLevel);
        setLocalLateRegCountDown(serverLateReg);
        return serverElapsed;
      }
      // Hold all three — let the local tick catch up smoothly.
      return localElapsed;
    });
  }, [liveStatus, parseCountdown]);

  // Tick every second — Level Timer, Elapsed, and Late Reg are all mutated in the
  // same interval callback so they advance atomically in lockstep. Skip ticking
  // entirely while the tournament is paused (break / post-balancing) so all three
  // values freeze alongside the engine clock; the next server sync absorbs any drift.
  useEffect(() => {
    if (!isPlaying || isPaused) return;

    timerRef.current = setInterval(() => {
      setLocalLevelCountDown((v) => Math.max(0, v - 1));
      setLocalElapsedTime((v) => v + 1);
      setLocalLateRegCountDown((v) => Math.max(0, v - 1));
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, isPaused]);

  // Current & next blind from structure
  const blindInfo = useMemo(() => {
    if (structuresData.length === 0) return null;
    const level = game.currentLevel === 0 ? liveStatus?.currentLevel : game.currentLevel;
    const current = structuresData.find((f) => Number(level) === Number(f.level));
    const next = structuresData.find((f) => Number(level) + 1 === Number(f.level));
    return { current, next, level };
  }, [structuresData, game.currentLevel, liveStatus?.currentLevel]);

  // At max level when we have a current row but no level+1 row in the
  // structure. Drives the "Max Level" labels for both Next Blind Level and
  // Level Countdown — otherwise those would read 'None' and '00:00' and
  // look like a broken state to the player.
  const isAtMaxLevel = !!blindInfo?.current && !blindInfo?.next;

  // Static tournament data (blind structure + prize ladder) — fetched once per
  // tournament, independent of whether the info panel is open.
  useEffect(() => {
    structures(tournamentId);
    if (!showSngPayouts) tournamentPrizes(tournamentId);
  }, [tournamentId, showSngPayouts]);

  // Tournament-info panel (S2) feed subscription. Subscribe WHILE the panel is
  // open; unsubscribe when it closes/unmounts. `isOpen` is a dependency so it
  // subscribes the moment the panel opens (not just if it happened to mount
  // open) and the cleanup only ever fires for a subscribe it actually made —
  // keeping the shared GameService ref-count balanced. When the player is
  // registered / seated, the App-level centralized effect holds its own count on
  // this tournament, so closing the panel only decrements and never tears the
  // live feed down for them.
  useEffect(() => {
    if (!isOpen) return undefined;
    subscribeTournamentDetails(tournamentId);
    return () => {
      unsubscribeTournamentDetails(tournamentId);
    };
  }, [tournamentId, isOpen, subscribeTournamentDetails, unsubscribeTournamentDetails]);

  const ownPayouts = useSngPayouts(tournamentId, showSngPayouts && !sngPayouts, isOpen);
  const payouts = sngPayouts ?? ownPayouts;

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleRanking = (data: any) => {
      try {
        data = decompressData(data);
        const r = (data?.response || []).filter((f: any) => f.tournamentId === tournamentId);
        if (r.length > 0) setRankPlayers(r);
      } catch (e) {
        console.warn('[TournamentDetails] ranking parse error', e);
      }
    };
    const handlePrizes = (data: any) => {
      try {
        data = decompressData(data);
        const p = (data?.response || []).filter((f: any) => f.tournamentId === tournamentId);
        if (p.length > 0) setPrizes(p);
      } catch (e) {
        console.warn('[TournamentDetails] prizes parse error', e);
      }
    };
    const handleStructure = (data: any) => {
      try {
        data = decompressData(data);
        let s = data?.response;
        if (Array.isArray(s) && Array.isArray(s[0])) s = s[0];
        const filtered = (s || []).filter((f: any) => f.tournamentId === tournamentId);
        if (filtered.length > 0) setStructuresData(filtered);
      } catch (e) {
        console.warn('[TournamentDetails] structure parse error', e);
      }
    };
    const handleTables = (data: any) => {
      try {
        data = decompressData(data);
        const t = (data?.response || []).filter((f: any) => f.tournamentId === tournamentId);
        if (t.length > 0) setActiveTables(t);
      } catch (e) {
        console.warn('[TournamentDetails] tables parse error', e);
      }
    };

    socket.on(GOT_TOURNAMENT_RANKING, handleRanking);
    socket.on(GOT_TOURNAMENT_PRIZES, handlePrizes);
    socket.on(GOT_TOURNAMENT_STRUCTURE, handleStructure);
    socket.on(GOT_TOURNAMENT_ACTIVE_TABLES_V2, handleTables);

    return () => {
      socket.off(GOT_TOURNAMENT_RANKING, handleRanking);
      socket.off(GOT_TOURNAMENT_PRIZES, handlePrizes);
      socket.off(GOT_TOURNAMENT_STRUCTURE, handleStructure);
      socket.off(GOT_TOURNAMENT_ACTIVE_TABLES_V2, handleTables);
    };
  }, [socket, tournamentId]);

  if (!isOpen) return null;

  const tabs: { key: Tab; label: string }[] = isPlaying
    ? [
        { key: 'ranking', label: 'Ranking' },
        { key: 'prizes', label: 'Prizes' },
        { key: 'tables', label: 'Tables' },
        { key: 'blinds', label: 'Blinds' }
      ]
    : [
        { key: 'ranking', label: 'Players' },
        ...(showSngPayouts ? [{ key: 'prizes' as Tab, label: 'Prizes' }] : []),
        { key: 'blinds', label: 'Blinds' }
      ];

  const lateRegistrationValue =
    status === 'R'
      ? 'Open'
      : !isPlaying
      ? '-'
      : localLateRegCountDown <= 0
      ? 'Ended'
      : humanizeCountdown(localLateRegCountDown);

  // Blinds climb into the hundreds of thousands, so abbreviate (100000 → "100K")
  // to keep the level readout consistent with the on-felt display and readable.
  const formatBlinds = (sb: number, bb: number, ante: number) =>
    `${compactChips(sb)}/${compactChips(bb)} (${compactChips(ante)})`;

  return (
    <div style={backdropStyle} onClick={closeDetails}>
      <div
        style={{ ...panelStyle, maxHeight: portraitLocked ? '85vw' : '85vh' }}
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <EmojiEventsIcon style={{ fontSize: '1.2rem', color: '#E98C26' }} />
          <span style={titleStyle}>{gameTitle}</span>
          <div style={{ flex: 1 }} />
          <button onClick={closeDetails} style={closeBtnStyle}>
            <CloseIcon style={{ fontSize: '1rem' }} />
          </button>
        </div>

        {/* Row 1: Blind Level / Next Blind Level / Player·Entry·AddOn */}
        <div style={statsBarStyle}>
          <StatBox
            label='Blind Level'
            value={
              !isPlaying || liveStatus?.isMultiFlightBreak
                ? '-'
                : liveStatus?.isBreak
                ? 'Break'
                : blindInfo?.current
                ? formatBlinds(blindInfo.current.sb, blindInfo.current.bb, blindInfo.current.ante)
                : '-'
            }
          />
          <StatBox
            label='Next Blind Level'
            value={
              !isPlaying || liveStatus?.isMultiFlightBreak
                ? '-'
                : blindInfo?.next?.isBreak
                ? 'Break'
                : blindInfo?.next
                ? formatBlinds(blindInfo.next.sb, blindInfo.next.bb, blindInfo.next.ante)
                : isAtMaxLevel
                ? 'Max Level'
                : 'None'
            }
          />
          {(() => {
            const { label, value } = formatPlayerEntryAddonStat(liveStatus, isPlaying);
            return <StatBox label={label} value={value} />;
          })()}
        </div>

        {/* Row 2: Prize Pool / Level Countdown / Elapsed Time */}
        <div style={statsBarStyle}>
          <StatBox
            label='Prize Pool'
            // Before anyone registers the status feed has no row for the
            // tournament, so fall back to the caller's pool (the lobby's
            // guarantee-inclusive "Current prize pool") rather than $0.00.
            value={
              liveStatus?.isMultiFlightBreak
                ? '-'
                : currencyFormatter(
                    showSngPayouts
                      ? payoutPool(payouts.rows) ?? tournamentPrizePool
                      : liveStatus?.prizePool ?? tournamentPrizePool ?? 0
                  )
            }
          />
          <StatBox
            label='Level Countdown'
            value={
              !isPlaying || liveStatus?.isPostBalancing
                ? '-'
                : isAtMaxLevel
                ? 'Max Level'
                : secondsToTime(localLevelCountDown)
            }
          />
          <StatBox
            label='Elapsed Time'
            value={
              isPlaying
                ? secondsToTime(localElapsedTime)
                : status === 'R'
                ? 'Registering'
                : status === 'A'
                ? 'Announced'
                : 'Finished'
            }
          />
        </div>

        {/* Row 3: Late Registration (MTT only). With an add-on it becomes a
            stat box beside the Add-On box — fee and chips, so a seated player
            can fund it ahead of the short add-on break (EN-10960). Without one
            (or with the flag off) it stays the full-width, label-left row. */}
        {tableType === 'm' && (
          <div style={statsBarStyle}>
            {addOnInfo ? (
              <>
                <StatBox label='Late Registration' value={lateRegistrationValue} />
                <StatBox label='Add-On' value={addOnInfo} />
              </>
            ) : (
              <WideStatRow label='Late Registration' value={lateRegistrationValue} />
            )}
          </div>
        )}

        {/* Row 4: Stacks — full-width group with 4 inline sub-stats.
            K/M truncation via compactChips (EN-10415): deep-stack tourneys
            climb into 7-8 figures, so raw comma-grouped values overflow the
            narrow inline sub-stat columns. `total` in particular is total
            chips in play across every entrant × starting stack — 100M+ is
            routine. Matches the truncation already used on the level's
            SB/BB (formatBlinds via compactChips) so every number in the
            tournament header reads the same way. */}
        <div style={statsBarStyle}>
          <StacksRow
            average={isPlaying ? compactChips(liveStatus?.average ?? 0) : '-'}
            largest={isPlaying ? compactChips(liveStatus?.largest ?? 0) : '-'}
            smallest={isPlaying ? compactChips(liveStatus?.smallest ?? 0) : '-'}
            total={isPlaying ? compactChips(liveStatus?.total ?? 0) : '-'}
          />
        </div>

        {/* Tabs */}
        <div style={tabBarStyle}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSelectedTab(tab.key)}
              style={selectedTab === tab.key ? { ...tabStyle, ...tabActiveStyle } : tabStyle}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content — the one scroll container behind all four tabs
            (Ranking / Prizes / Tables / Blinds), so tagging it here covers
            every one of them. Owns its own scroll: the table-swipe Observer
            must hand wheel gestures back to the browser rather than
            preventDefault them. See utils/swipeIgnore. */}
        <div style={tabContentStyle} {...swipeIgnoreProps}>
          {selectedTab === 'ranking' && (
            <RankingTab players={rankPlayers} guid={guid} tableType={tableType} status={status} />
          )}
          {selectedTab === 'prizes' &&
            (showSngPayouts ? (
              <TournamentPayouts
                variant='info'
                {...payouts}
                seats={Number(maxSeats ?? liveStatus?.maxSeat)}
                bountyAmount={bountyAmount ?? liveStatus?.bountyAmount}
              />
            ) : (
              <PrizesTab prizes={prizes} status={status} />
            ))}
          {selectedTab === 'tables' && (
            <TablesTab
              tables={activeTables}
              currentGameID={currentGameID}
              openTableIds={openTableIds}
              isAtCap={isAtCap}
              holdsSlot={holdsSlot}
              isActivePlayer={isActivePlayer}
              guest={guest}
              onObserve={(gid, g) => {
                // In multi-table (grid) view, observing another table must ADD
                // it to the grid and stay in grid — NOT navigate to the
                // single-table view (which collapsed the grid). Subscribe to the
                // table so it lands in state.tables (→ a new grid cell), then
                // select it. The subscription persists (it isn't in the
                // auto-resub set App.tsx prunes), so the cell stays put.
                if (isGridMode) {
                  joinTable(String(gid), g || 'texas');
                  selectGridTable(String(gid));
                  return;
                }
                openTable(String(gid), g, 'yes');
              }}
            />
          )}
          {selectedTab === 'blinds' && (
            <BlindsTab
              structures={structuresData}
              currentLevel={liveStatus?.currentLevel ?? 0}
              status={status}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default TournamentDetails;

/* ── Sub-components ── */

// Wide single-stat row: "Label  Value" laid out left-to-right across the full panel.
// Exported for unit testing.
export const WideStatRow = ({ label, value }: { label: string; value: string }) => (
  <div style={wideRowStyle}>
    <span style={wideRowLabelStyle}>{label}</span>
    <span style={wideRowValueStyle}>{value}</span>
  </div>
);

// Wide grouped row: a "Stacks" category label on the left, followed by 4 inline
// mini-stats (Average / Largest / Smallest / Total) each rendered as label-above-value.
// Exported for unit testing.
export const StacksRow = ({
  average,
  largest,
  smallest,
  total
}: {
  average: string;
  largest: string;
  smallest: string;
  total: string;
}) => (
  <div style={wideRowStyle}>
    <span style={wideRowLabelStyle}>Stacks</span>
    <div style={stacksInlineContainerStyle}>
      <StacksMiniStat label='Average' value={average} />
      <StacksMiniStat label='Largest' value={largest} />
      <StacksMiniStat label='Smallest' value={smallest} />
      <StacksMiniStat label='Total' value={total} />
    </div>
  </div>
);

const StacksMiniStat = ({ label, value }: { label: string; value: string }) => (
  <div style={stacksMiniStatStyle}>
    <span style={stacksMiniLabelStyle}>{label}</span>
    <span style={stacksMiniValueStyle}>{value}</span>
  </div>
);

const StatBox = ({
  label,
  value,
  highlight
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) => (
  <div style={statBoxStyle}>
    <span style={statLabelStyle}>{label}</span>
    <span style={{ ...statValueStyle, ...(highlight ? { color: '#E98C26' } : {}) }}>{value}</span>
  </div>
);

const RankingTab = ({
  players,
  guid,
  tableType,
  status
}: {
  players: any[];
  guid: string;
  tableType: string;
  status: string;
}) => {
  if (players.length === 0) return <EmptyTab status={status} />;
  const isSng = tableType === 't';
  const rowSpacing: CSSProperties = isSng
    ? {
        display: 'grid',
        gridTemplateColumns: 'minmax(4rem, 1fr) minmax(0, 3fr) minmax(4rem, 1fr)',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        borderRadius: 0
      }
    : {};
  const rankingCell = (width: string, align: 'left' | 'right' = 'left'): CSSProperties =>
    isSng
      ? {
          display: 'block',
          minWidth: 0,
          textAlign: align,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }
      : { ...cellStyle, width };
  return (
    <div style={isSng ? { marginInline: '-1rem', width: 'calc(100% + 2rem)' } : undefined}>
      <div
        style={{
          ...tableHeaderRow,
          ...rowSpacing,
          ...(isSng
            ? ({
                fontSize: '0.6rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'rgba(255, 255, 255, 0.45)'
              } as CSSProperties)
            : {})
        }}>
        <span style={rankingCell('15%')}>Ranking</span>
        <span style={{ ...rankingCell('45%'), textAlign: 'left' }}>Name</span>
        <span style={rankingCell('25%', 'right')}>Chips</span>
        {tableType === 'm' && <span style={{ ...cellStyle, width: '15%' }}>R+A</span>}
      </div>
      {players.map((p: any, i: number) => {
        const isMe = p.playerId === guid;
        return (
          <div
            key={i}
            style={{
              ...tableRow,
              ...(isSng ? { fontWeight: 600 } : {}),
              ...(isMe ? highlightRow : {}),
              ...rowSpacing
            }}>
            <span style={{ ...rankingCell('15%'), fontWeight: i < 3 ? 700 : 400 }}>
              {i < 3 ? ['🥇', '🥈', '🥉'][i] : i + 1}
            </span>
            <span
              style={{
                ...rankingCell('45%'),
                textAlign: 'left',
                fontWeight: isMe ? 700 : isSng ? 600 : 400
              }}>
              {p.displayName}
              {isMe ? ' (You)' : ''}
            </span>
            <span style={rankingCell('25%', 'right')}>{compactChips(p.finalHandPot)}</span>
            {tableType === 'm' && <span style={{ ...cellStyle, width: '15%' }}>{p.ra}</span>}
          </div>
        );
      })}
    </div>
  );
};

const PrizesTab = ({ prizes, status }: { prizes: any[]; status: string }) => {
  if (prizes.length === 0) return <EmptyTab status={status} />;
  return (
    <div>
      <div style={tableHeaderRow}>
        <span style={{ ...cellStyle, width: '40%' }}>Ranking</span>
        <span style={{ ...cellStyle, width: '60%' }}>Rewards</span>
      </div>
      {prizes.map((p: any, i: number) => (
        <div key={i} style={tableRow}>
          <span style={{ ...cellStyle, width: '40%' }}>{p.rank}</span>
          <span style={{ ...cellStyle, width: '60%', color: '#E98C26' }}>
            {p.isTicket ? 'Ticket' : `$${p.percentPrizePool}`}
          </span>
        </div>
      ))}
    </div>
  );
};

// Exported for unit testing.
export const TablesTab = ({
  tables,
  currentGameID,
  openTableIds,
  isAtCap,
  holdsSlot,
  isActivePlayer,
  guest = false,
  onObserve
}: {
  tables: any[];
  /** The table being viewed from, or undefined when it is not (or no longer) open. */
  currentGameID: number | undefined;
  openTableIds: Set<string>;
  isAtCap: boolean;
  /** Whether a table already holds a slot (opening it takes no new one). */
  holdsSlot?: (gameID: string, game: string) => boolean;
  isActivePlayer: boolean;
  guest?: boolean;
  onObserve: (gid: number, game: string) => void;
}) => {
  if (tables.length === 0) return <div style={emptyStyle}>No active tables</div>;
  // Active players ('P') can't observe other tables, and guests can't
  // observe tournament tables at all — hide the column entirely and widen
  // the remaining ones so the row still reads cleanly.
  const hideObserve = isActivePlayer || guest;
  const cols = hideObserve
    ? { num: '30%', players: '30%', stack: '40%' }
    : { num: '25%', players: '25%', stack: '30%', observe: '20%' };
  return (
    <div>
      <div style={tableHeaderRow}>
        <span style={{ ...cellStyle, width: cols.num }}>Table #</span>
        <span style={{ ...cellStyle, width: cols.players }}>No. of Players</span>
        <span style={{ ...cellStyle, width: cols.stack }}>Low / Hi Stack</span>
        {!hideObserve && <span style={{ ...cellStyle, width: cols.observe }}>Observe</span>}
      </div>
      {tables.map((t: any, i: number) => {
        // A table is "Observed" if the player is seated at it (the
        // tournament's currentGameID) OR if it's currently in the multi-
        // table redux slice (open as an observed/watched table).
        const isCurrentTable = t.gameID === currentGameID;
        const isOpen = openTableIds.has(String(t.gameID));
        const showObservedLabel = isCurrentTable || isOpen;
        // Disable the Observe button at the 4-table cap so users get a
        // clear visual signal rather than a click-then-modal flow. Already-
        // open rows show "Observed" instead, so they're naturally exempt, and
        // a table that already holds a slot opens into it.
        const observeDisabled =
          isAtCap && !showObservedLabel && !holdsSlot?.(String(t.gameID), t.game || 'texas');
        return (
          <div key={i} style={{ ...tableRow, ...(showObservedLabel ? highlightRow : {}) }}>
            <span style={{ ...cellStyle, width: cols.num }}>#{i + 1}</span>
            <span style={{ ...cellStyle, width: cols.players }}>
              <PeopleIcon style={{ fontSize: '0.75rem', marginRight: 4 }} />
              {t.noOfPlayers}
            </span>
            <span style={{ ...cellStyle, width: cols.stack }}>
              {compactChips(t.lowestStack)} / {compactChips(t.largestStack)}
            </span>
            {!hideObserve && (
              <span style={{ ...cellStyle, width: cols.observe }}>
                {showObservedLabel ? (
                  <span style={observedLabelStyle}>Observed</span>
                ) : (
                  <button
                    style={observeDisabled ? observeBtnDisabledStyle : observeBtnStyle}
                    onClick={() => onObserve(t.gameID, t.game)}
                    disabled={observeDisabled}>
                    Observe
                  </button>
                )}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const BlindsTab = ({
  structures,
  currentLevel,
  status
}: {
  structures: TournamentStructure[];
  currentLevel: number;
  status: string;
}) => {
  if (structures.length === 0) return <EmptyTab status={status} />;
  return (
    <div>
      <div style={tableHeaderRow}>
        <span style={{ ...cellStyle, width: '20%' }}>Level</span>
        <span style={{ ...cellStyle, width: '25%' }}>Time</span>
        <span style={{ ...cellStyle, width: '35%' }}>Blinds</span>
        <span style={{ ...cellStyle, width: '20%' }}>Ante</span>
      </div>
      {structures.map((s: any, i: number) => {
        const isCurrent = Number(currentLevel) === Number(s.level) && status === 'P';
        return (
          <div
            key={i}
            style={{ ...tableRow, ...(isCurrent ? { color: '#E98C26', fontWeight: 700 } : {}) }}>
            <span style={{ ...cellStyle, width: '20%' }}>{s.isBreak ? 'Break' : s.lvlDisplay}</span>
            <span style={{ ...cellStyle, width: '25%' }}>{s.mins}m</span>
            <span style={{ ...cellStyle, width: '35%' }}>
              {s.isBreak ? '-' : `${compactChips(s.sb)}/${compactChips(s.bb)}`}
            </span>
            <span style={{ ...cellStyle, width: '20%' }}>
              {s.isBreak ? '-' : compactChips(s.ante)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const EmptyTab = ({ status }: { status: string }) => (
  <div style={emptyStyle}>
    {status === 'R' ? 'Registering...' : status === 'A' ? 'Announced' : 'Loading...'}
  </div>
);

/* ── Styles ── */

const backdropStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
  padding: '1rem'
};

const panelStyle: CSSProperties = {
  width: '100%',
  maxWidth: '520px',
  maxHeight: '85vh',
  backgroundColor: 'rgba(20, 30, 42, 0.98)',
  borderRadius: '16px',
  boxShadow: '0 16px 48px rgba(0, 0, 0, 0.6)',
  border: '1px solid rgba(233, 140, 38, 0.15)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  fontFamily: "'Outfit', sans-serif",
  color: 'rgba(255, 255, 255, 0.9)'
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '1rem 1.25rem 0.75rem'
};

const titleStyle: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 700,
  color: '#fff',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const closeBtnStyle: CSSProperties = {
  background: 'rgba(255, 255, 255, 0.06)',
  border: 'none',
  borderRadius: '8px',
  color: 'rgba(255, 255, 255, 0.5)',
  padding: '0.35rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
};

const statsBarStyle: CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
  padding: '0 1rem',
  marginBottom: '0.5rem'
};

const statBoxStyle: CSSProperties = {
  flex: 1,
  // Let the box shrink to its share of the row and wrap long values
  // ("1 hour 37 mins 34 secs", "$1.00 for 500 chips") inside it instead of
  // spilling past its edge on narrow screens.
  minWidth: 0,
  textAlign: 'center',
  overflowWrap: 'anywhere',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  padding: '0.6rem 0.5rem',
  backgroundColor: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '6px'
};

const statLabelStyle: CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 600,
  color: '#E98C26',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const statValueStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.9)'
};

// Wide row styles — used for "Late Registration" and "Stacks" rows.
const wideRowStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  padding: '0.6rem 0.8rem',
  backgroundColor: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '6px'
};

const wideRowLabelStyle: CSSProperties = {
  fontSize: '0.6rem',
  fontWeight: 600,
  color: '#E98C26',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  flexShrink: 0
};

const wideRowValueStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.9)'
};

const stacksInlineContainerStyle: CSSProperties = {
  display: 'flex',
  flex: 1,
  justifyContent: 'space-around',
  gap: '0.5rem'
};

const stacksMiniStatStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2px'
};

const stacksMiniLabelStyle: CSSProperties = {
  fontSize: '0.55rem',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.5)',
  textTransform: 'capitalize'
};

const stacksMiniValueStyle: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.9)'
};

const tabBarStyle: CSSProperties = {
  display: 'flex',
  gap: '2px',
  padding: '0.5rem 1rem 0',
  borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
};

const tabStyle: CSSProperties = {
  flex: 1,
  padding: '0.5rem 0',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  color: 'rgba(255, 255, 255, 0.4)',
  fontSize: '0.7rem',
  fontWeight: 700,
  fontFamily: 'inherit',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  cursor: 'pointer',
  transition: 'all 0.15s ease'
};

const tabActiveStyle: CSSProperties = {
  color: '#E98C26',
  // Use the borderBottom shorthand (matching tabStyle) rather than the
  // borderBottomColor longhand — mixing shorthand + longhand for the same
  // property triggers React's "removing a style property during rerender"
  // warning when a tab toggles active/inactive.
  borderBottom: '2px solid #E98C26'
};

const tabContentStyle: CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '0.5rem 1rem 1rem',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255,255,255,0.1) transparent'
};

const tableHeaderRow: CSSProperties = {
  display: 'flex',
  padding: '0.4rem 0',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
};

const tableRow: CSSProperties = {
  display: 'flex',
  padding: '0.4rem 0',
  borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.7)'
};

const highlightRow: CSSProperties = {
  backgroundColor: 'rgba(233, 140, 38, 0.08)',
  borderRadius: '4px',
  fontWeight: 700,
  color: '#E98C26'
};

const cellStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem'
};

const emptyStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  color: 'rgba(255, 255, 255, 0.3)',
  fontSize: '0.85rem'
};

const observeBtnStyle: CSSProperties = {
  background: 'rgba(255, 255, 255, 0.08)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '6px',
  color: 'rgba(255, 255, 255, 0.6)',
  fontSize: '0.6rem',
  fontWeight: 600,
  padding: '3px 8px',
  cursor: 'pointer',
  fontFamily: 'inherit'
};

const observeBtnDisabledStyle: CSSProperties = {
  ...observeBtnStyle,
  opacity: 0.4,
  cursor: 'not-allowed',
  color: 'rgba(255, 255, 255, 0.3)'
};

const observedLabelStyle: CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 600,
  color: '#E98C26',
  letterSpacing: '0.05em',
  textTransform: 'uppercase'
};
